<template>
    <div class="bg-surface-secondary w-100">
        <header class="bg-surface-primary border-bottom">
            <div class="container-fluid py-4">
                <div class="row align-items-center">
                    <div class="col">
                        <div class="d-flex align-items-center gap-4">
                            <div>
                                <button type="button" class="btn-close text-xs" @click="$router.go(-1)"></button>
                            </div>
                            <div class="vr opacity-20 my-1"></div>
                            <h1 class="h4 ls-tight">Crear Prealerta</h1>
                        </div>
                    </div>
                    <div class="col-auto">
                        <div class="hstack gap-2 justify-content-end">
                            <button @click="CREATE()" type="button" class="btn btn-h btn-success px-3">
                                <div class="spinner-border spinner-border-sm" role="status" v-if="count == 1">
                                    <span class="sr-only">Loading...</span>
                                </div>
                                <span v-else>Guardar</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </header>
        <main class="py-6 bg-surface-secondary">
            <div class="container-fluid vstack gap-5">
                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group mb-2">
                            <small class="input-title">Tracking</small>
                            <div class="input-group input-group-sm">
                                <div class="input-group-prepend">
                                    <span class="input-group-text">
                                        #
                                    </span>
                                </div>
                                <input type="text" class="form-control form-control-sm" v-model="data.tracking"
                                    :class="errors.tracking ? 'is-invalid' : ''">
                                <small class="invalid-feedback" v-for="(error, index) in errors.tracking"
                                    :key="index">{{ error }}</small>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group mb-2">
                            <small class="input-title">Transportista</small>
                            <v-select v-model="data.carrier_id" :items="carriers" item-text="name" item-value="id"
                                hide-details no-data class="form-control form-control-sm"
                                :class="errors.carrier_id ? 'is-invalid' : ''">
                            </v-select>
                            <small class="invalid-feedback" v-for="(error, index) in errors.carrier_id" :key="index">{{
                                    error }}</small>
                        </div>
                    </div>
                    <div class="col-md-12">
                        <div class="form-group mb-2">
                            <small class="input-title">Descripción</small>
                            <v-combobox v-model="data.description" chips hide-details multiple clearable
                                :delimiters="[',']" :search-input.sync="search_description"
                                @update:search-input="MEOW_INPUT" class="form-control form-control-sm textarea"
                                :class="errors.description ? 'is-invalid' : ''">
                                <template v-slot:selection="{ attrs, item, select, selected }">
                                    <v-chip small v-bind="attrs" close @click:close="REMOVE(item)"
                                        :input-value="selected">
                                        <div class="nowrap">{{ item }}</div>
                                    </v-chip>
                                </template>
                            </v-combobox>
                            <small class="invalid-feedback" v-for="(error, index) in errors.description" :key="index">{{
                                    error }}</small>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group mb-2">
                            <small class="input-title">Remitente (tienda)</small>
                            <search-shipper v-model="data.shipper" :childclass="errors.shipper ? 'is-invalid' : ''" />
                            <small class="invalid-feedback" v-for="(error, index) in errors.shipper" :key="index">{{
                                    error }}</small>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group mb-2">
                            <small class="input-title">Valor</small>
                            <div class="input-group input-group-sm">
                                <input type="text" class="form-control form-control-sm" @keypress="DOUBLE"
                                    v-model="data.value" :class="errors.value ? 'is-invalid' : ''">
                                <div class="input-group-append">
                                    <span class="input-group-text bg-white">
                                        $
                                    </span>
                                </div>
                                <small class="invalid-feedback" v-for="(error, index) in errors.value" :key="index">{{
                                    error }}</small>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6" v-if="user.is_exemption">
                        <div class="form-group mb-2">
                            <small class="input-title">Exención de Impuestos*</small>
                            <div class="input-group input-group-sm">
                                <v-checkbox v-model="data.exemption" value="requested"
                                    :class="errors.exemption ? 'is-invalid' : ''">
                                    <template v-slot:label>
                                        <small class="font-weight-bold my-auto">
                                            Solicitar Trámite de Exención de Impuestos (+$20.00)
                                        </small>
                                    </template>
                                </v-checkbox>
                                <small class="invalid-feedback" v-for="(error, index) in errors.exemption"
                                    :key="index">{{
                                    error
                                }}</small>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group mb-2">
                            <small class="input-title">Subir Archivo</small>
                            <v-file-input hide-details truncate-length="50" multiple v-model="fileInput"
                                accept="application/pdf, image/*" @change="UPLOAD_FILES"
                                :class="errors.files ? 'is-invalid border-danger' : ''"
                                class="form-control form-control-sm cursor-pointer p-0"
                                :placeholder="'Debe adjuntar la factura del producto y copia de su documento de identificación'">
                            </v-file-input>
                            <small class="invalid-feedback" v-for="(error, index) in errors.files" :key="index">{{
                                    error
                                }}</small>
                        </div>
                        <div class="mb-1" v-for="(item, index) in data.files" :key="index">
                            <div class="card">
                                <a :href="`${item.path}`" target="_blank" rel="noopener noreferrer"
                                    class="card-body text-decoration-none">
                                    <div class="d-flex align-items-center justify-content-between">
                                        <div class="d-flex flex-column">
                                            <small class="mb-1 text-decoration-none"><b>{{ item.name }}</b></small>
                                            <div class="text-muted d-flex"><small class="mr-3"
                                                    style="font-size: 10px;">size: {{ item.size |
                                    SIZE }}</small><small style="font-size: 10px;">type: {{
                                    item.name.split(".").pop()
                                }}</small></div>
                                        </div>
                                        <a href="#" @click="REMOVE_FILE(item.id, index)"
                                            class="text-dark font-weight-bold text-decoration-none"><i
                                                class="fa-solid fa-close"></i></a>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                    <div class="col-12"></div>
                    <div class="col-6 d-flex">
                        <small style="font-size: 10px;" class="text-muted text-justify">* El trámite tiene un costo de
                            20 Dólares Americanos
                            (20.00 USD). <br>Se puede realizar máximo una vez cada 6 meses.<br> Debe adjuntar la factura
                            del producto y
                            copia de su documento de identificación.<br> De no cumplir con los requisitos el trámite
                            será rechazado.</small>
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>
<script>
import { mapGetters } from 'vuex'
import Compressor from 'compressorjs'
export default {
    data() {
        let random = Math.floor((Math.random() * 99999999) + 10000000)
        return {
            data: {
                carrier_id: 1,
                description: [],
                shipper: 'Amazon',
                files: [],
            },
            errors: {},
            actions: [
                { value: 0, text: 'No' },
                { value: 1, text: 'Si' }
            ],
            carriers: [],
            packings: [],
            loading: '',
            search_description: '',
            count: 0,
            fileInput: [],
        }
    },
    created() {
        let vue = this
        vue.CARRIERS()
        vue.PACKINGS()
    },
    computed: {
        ...mapGetters([
            'user',
        ])
    },
    methods: {
        UPLOAD_FILES: function (e) {
            let _this = this
            if (e) {
                e.forEach(element => {
                    new Compressor(element, {
                        quality: 0.3,
                        success(result) {
                            const fileReader = new FileReader()
                            fileReader.addEventListener('load', () => {
                                const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
                                    const byteCharacters = atob(b64Data);
                                    const byteArrays = [];

                                    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
                                        const slice = byteCharacters.slice(offset, offset + sliceSize);

                                        const byteNumbers = new Array(slice.length);
                                        for (let i = 0; i < slice.length; i++) {
                                            byteNumbers[i] = slice.charCodeAt(i);
                                        }

                                        const byteArray = new Uint8Array(byteNumbers);
                                        byteArrays.push(byteArray);
                                    }

                                    const blob = new Blob(byteArrays, { type: contentType });
                                    return blob;
                                }
                                const contentType = 'image/png';
                                const b64Data = fileReader.result.split('base64,');
                                const blob = b64toBlob(b64Data[1], contentType);

                                result.path = URL.createObjectURL(blob)
                                _this.data.files.push(result)
                            })
                            fileReader.readAsDataURL(result)
                        },
                        error(err) {
                        }
                    })
                })
                if (this.fileInput.length > 0) {
                    this.fileInput = []
                }
            }
        },
        CLEAR: function () {
            let vue = this
            vue.data = {
                packing_id: 1,
                description: [],
                carrier_id: 1,
                shipper: 'Amazon',
                files: [],
            }
        },
        CREATE: function () {
            let vue = this
            vue.count++
            if (vue.count == 1) {
                vue.errors = {}
                let form = new FormData()
                $.each(vue.data, function (key, item) {
                    if (item != null) {
                        if (key == 'description') {
                            form.append(key, JSON.stringify(item))
                        } else if (key == 'files') {
                            $.each(item, function (i, file) {
                                form.append('files[' + i + ']', file)
                            })
                        } else {
                            form.append(key, item)
                        }
                    }
                })
                axios({ url: '/clients/packages', method: 'POST', data: form })
                    .then((resp) => {
                        if (vue.data.exemption == 'requested') {
                            this.user.is_exemption = false
                        }
                        vue.$swal({ toast: true, icon: 'success', title: resp.data.message, position: 'top-end', showConfirmButton: false, timer: 3000 })
                        vue.CLEAR()
                    })
                    .catch((err) => {
                        if (err.response.status !== 422) {
                            let message = err.response ? err.response.data.message : err.message
                            vue.$swal({ toast: true, icon: 'error', title: message, position: 'top-end', showConfirmButton: false, timer: 3000 })
                        } else {
                            vue.errors = err.response.data
                        }
                    })
                    .finally((resp) => {
                        vue.count = 0
                    })
            }
        },
        CARRIERS: function () {
            let vue = this
            axios({ url: '/all/carriers', method: 'GET' })
                .then((resp) => {
                    vue.carriers = resp.data
                })
        },
        PACKINGS: function () {
            axios({ url: '/all/packings', method: 'GET' })
                .then((resp) => {
                    this.packings = resp.data
                })
        },
        REMOVE: function (item) {
            if (this.data.description) {
                this.data.description.splice(this.data.description.indexOf(item), 1)
            }
        },
        REMOVE_FILE: function (index) {
            if (this.data.files) {
                this.data.files.splice(index, 1)
            }
        },
        MEOW_INPUT(e1) {
            if (this.search_description && this.search_description.split(",").length > 1) {
                this.data.description = this.data.description.concat(
                    this.search_description.split(",").filter((term) => !this.data.description.includes(term))
                );
                this.search_description = "";
            }
        },
    }
}
</script>